import React from 'react';
import { InfoOutlined } from '@mui/icons-material';
import { Box, FormControl, Tooltip, Typography, styled, tooltipClasses } from '@mui/material';

/**
 * Helper function to handle the change of a positive integer in a text field
 * @param {*} number - The number to check
 * @param {*} setter - The state setter function
 * @param {*} noLeading - Whether to allow leading zeros
 */
export const handlePositiveIntChange = (number, setter, noLeading) => {
    if (!number) {
        setter('');
        return '';
    }

    if (number.match(/^[0]+\d+$/) && noLeading) {
        setter(number.slice(1));
        return number.slice(1);
    }

    if (number.match(/^\d+$/)) {
        setter(number);
        return number;
    }

    setter('');
    return '';
};

/**
 * Styles for the tooltip. This is the suggested method by MUI
 *
 * @param {*} props
 * @returns
 * @see https://mui.com/components/tooltips/#customized-tooltips
 */
export const HtmlTooltip = styled(({ className, ...props }) => <Tooltip {...props} classes={{ popper: className }} />)(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.secondary.main,
        [`& .${tooltipClasses.arrow}`]: {
            color: theme.palette.secondary.main,
        },
        color: theme.palette.secondary.contrastText,
        maxWidth: 400,
        padding: theme.spacing(2),
        fontSize: theme.typography.pxToRem(12),
        border: '1px solid #dadde9',
    },
}));

/**
 * Tooltip for the po padding options
 * @param {*} params
 * @param {*} params.poLocalizationLabel - Label for the PO number
 * @returns
 */
export const PaddingTooltip = ({ poLocalizationLabel }) => {
    return (
        <Box sx={{ padding: (theme) => theme.spacing(1) }}>
            <Typography variant="h5" pb={2} borderBottom="1px solid white">
                {poLocalizationLabel} auto increment
            </Typography>
            <Typography variant="body1" pb={2}>
                This feature allows you to automatically increment the {poLocalizationLabel} by a set amount. {/* Wrap */}
                from the starting value. You can use both a set end date and the number of occurrences to control the {/* Wrap */}
                end of the sequence.
            </Typography>
            <Typography variant="body1" pb={2}>
                You can add extra leading zeros to the {poLocalizationLabel}. Select an amount of padding zeros to add {/* Wrap */}
                from the dropdown. For example when padding the next entry after <strong>0099</strong> would become <strong>0100</strong>.
            </Typography>
            <Typography variant="body1" pb={2}>
                The prefix and suffix will be added to the {poLocalizationLabel} after the padding zeros. For example if {/* Wrap */}
                <strong>999</strong> was given 5 padding zeros, a prefix of <strong>PO-</strong> and a suffix of {/* Wrap */}
                <strong>-MM</strong> the {poLocalizationLabel} sequence would be <strong>PO-00999-MM, PO-01000-MM...</strong>.
            </Typography>
        </Box>
    );
};

export function ToolTipIcon(props) {
    return (
        <div
            style={{
                paddingTop: '20px',
                // To offset of the label heights
                display: 'flex',
            }}
        >
            <HtmlTooltip title={<PaddingTooltip poLocalizationLabel={props.poLocalizationLabel} />} arrow placement="right">
                <InfoOutlined
                    color="info"
                    sx={{
                        margin: 'auto',
                    }}
                />
            </HtmlTooltip>
        </div>
    );
}

/**
 * Formats the FormRow component
 * @param {*} params
 * @param {*} params.children
 * @param {*} params.label - Label to display above the field
 * @returns
 */
export const FormRow = ({ label, children }) => {
    return (
        <FormControl variant="outlined" fullWidth>
            <Box>{label}</Box>
            {children}
        </FormControl>
    );
};

/**
 * Prevents the user from entering a negative number or a plus sign.
 * Not ideal but NumberInput is not ready in MUI yet.
 * @param {*} e - The event
 * @returns
 */
export const preventMinus = (e) => {
    if (e.key === '-' || e.key === '+') {
        e.preventDefault();
    }
};
